import React, { useState, useCallback } from "react";
import { getBezierPath, useStore } from "@xyflow/react";
import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const handleBaseStyle = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "2px solid white",
  backgroundColor: "rgba(128,128,128,0.6)", // gray background
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
  fontWeight: "bold",
  color: "white",
  cursor: "pointer",
  padding: 0,
  boxSizing: "border-box",
  userSelect: "none",
};

export const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  targetHandle,
  source,
  target,
  sourceHandle,
  ...props
}) => {
  const { onAddBranch, onDeleteEdge } = data || {};
  const [hovered, setHovered] = useState(false);
  const [buttonsHovered, setButtonsHovered] = useState(false);

  // Get the actual edge data from React Flow store to access targetHandle
  const edgeFromStore = useStore((state) => state.edges.find((edge) => edge.id === id));

  const onEdgeMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onEdgeMouseLeave = useCallback(() => {
    setTimeout(() => {
      if (!buttonsHovered) setHovered(false);
    }, 150);
  }, [buttonsHovered]);

  const onButtonsMouseEnter = useCallback(() => {
    setButtonsHovered(true);
    setHovered(true);
  }, []);

  const onButtonsMouseLeave = useCallback(() => {
    setButtonsHovered(false);
    setHovered(false);
  }, []);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Check if this edge connects to AI agent special handles (chatModel, memory, outputParser)
  // React Flow might pass targetHandle in different ways, let's check all of them
  const actualTargetHandle =
    targetHandle || props.targetHandle || data?.targetHandle || edgeFromStore?.targetHandle;
  const isAgentConnection =
    actualTargetHandle && ["chatModel", "memory", "outputParser"].includes(actualTargetHandle);

  return (
    <>
      <path
        id={id}
        style={{
          stroke: hovered ? "#FF6B6B" : style.stroke || "#888",
          strokeWidth: hovered ? 3 : style.strokeWidth || 1,
          strokeDasharray: isAgentConnection ? "5,5" : "none",
          fill: "none",
          cursor: "pointer",
          transition: "stroke 0.3s, stroke-width 0.3s",
          ...style,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        onMouseEnter={onEdgeMouseEnter}
        onMouseLeave={onEdgeMouseLeave}
      />

      {(hovered || buttonsHovered) && (
        <foreignObject
          width={56} // enough to hold two buttons side by side
          height={28}
          x={labelX - 28} // center on labelX
          y={labelY - 14} // center vertically on labelY
          style={{ overflow: "visible", pointerEvents: "auto" }}
          onMouseEnter={onButtonsMouseEnter}
          onMouseLeave={onButtonsMouseLeave}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              pointerEvents: "auto",
              userSelect: "none",
            }}
          >
            <Tooltip title="Add Branch">
              <IconButton
                aria-label="Add Branch"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddBranch?.(id);
                }}
                sx={handleBaseStyle}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete Link">
              <IconButton
                aria-label="Delete Link"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEdge?.(id);
                }}
                sx={handleBaseStyle}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </foreignObject>
      )}
    </>
  );
};
